






























import Vue from "vue";
import { Auth } from "aws-amplify";
import { isTwitterLogin } from "@/lib/twitterAuth";
import GlobalComponent from "@/components/GlobalComponent.vue";
import WarningIcon from "@/components/atoms/WarningIcon.vue";
import { showNotifyDialog } from "@/lib/dialog";
import { Dialog, isBilling } from "@/lib/utils";
import PopupDialog, { PopupDialogProps, Popup } from "@/components/ui/PopupDialog.vue";
import axiosBase from "axios";
import { Route } from "vue-router";
import { UserStatus, SubscribeStatus, LoginStatus } from "@/lib/userProperty";
import { Plan } from "./lib/models/user";

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { GlobalComponent, WarningIcon },
  async created() {
    const { $store, showErrorDialog, persistStorage } = this;
    $store.dispatch("generalModule/initialize");
    await persistStorage();

    try {
      const popupRequest = await axios.get(`/popup`);
      if (popupRequest.status !== 200) {
        await showErrorDialog();
        return;
      }
      this.popups = popupRequest.data.contents;
    } catch (e) {
      await showErrorDialog();
    }
  },
  data() {
    return {
      popups: [],
    };
  },
  computed: {
    readPopupList() {
      return this.$store.getters["generalModule/readPopupList"];
    },
    plan() {
      return this.$store.getters["userModule/plan"];
    },
  },
  methods: {
    close(id) {
      this.$notify.close(id);
    },
    async showErrorDialog() {
      await showNotifyDialog({
        title: "エラー",
        content: "ポップアップ情報の取得に失敗しました。",
      });
    },
    async persistStorage() {
      /**
       * NOTE: IndexedDBを永続化させる
       * https://dexie.org/docs/StorageManager#controlling-persistence
       */
      if (navigator.storage && navigator.storage.persist) await navigator.storage.persist();
    },
  },
  watch: {
    async $route(to: Route, from: Route) {
      const { $gtm, $store, $nextTick, popups, readPopupList, plan } = this;
      const { dataLayer } = window;

      const user = (await Auth.currentCredentials()) as any;
      const isAuthenticated = user.authenticated;
      const isLogin = user && !user.code && (isTwitterLogin() || isAuthenticated);

      let userStatus: string;
      let subscribeStatus: string;
      switch (plan) {
        case Plan.free:
          userStatus = UserStatus.value.freeUser;
          subscribeStatus = SubscribeStatus.value.none;
          break;
        case Plan.monthly:
          userStatus = UserStatus.value.subscriptionUser;
          subscribeStatus = SubscribeStatus.value.subscribeMonth;
          break;
        case Plan.yearly:
          userStatus = UserStatus.value.subscriptionUser;
          subscribeStatus = SubscribeStatus.value.subscribeYear;
          break;
        case Plan.school:
          userStatus = UserStatus.value.schoolUser;
          subscribeStatus = SubscribeStatus.value.none;
          break;
        default:
          userStatus = UserStatus.value.unRegister;
          subscribeStatus = SubscribeStatus.value.none;
          break;
      }

      // GTMのユーザープロパティを設定
      if ($gtm.enabled() && dataLayer) {
        if (isLogin) {
          const sub = user.signInUserSession ? user.signInUserSession.idToken.payload.sub : user.data.IdentityId;

          dataLayer.push({ user_id: sub });
          dataLayer.push({ [LoginStatus.key]: LoginStatus.value.loggedIn });
          dataLayer.push({ [UserStatus.key]: userStatus });
          dataLayer.push({ [SubscribeStatus.key]: subscribeStatus });
        } else {
          dataLayer.push({ user_id: null });
          dataLayer.push({ [LoginStatus.key]: LoginStatus.value.loggedOut });
          dataLayer.push({ [UserStatus.key]: UserStatus.value.unRegister });
          dataLayer.push({ [SubscribeStatus.key]: SubscribeStatus.value.none });
        }
        // ユーザープロパティをGTMからGA4に送信するためのイベント
        dataLayer.push({ event: "user_property_push" });
      }

      const toName = to.name;
      const fromName = from.name;
      const isTransitioned = toName !== fromName;
      if (!isTransitioned) return; // 画面遷移していない場合はreturn

      await $nextTick();

      const popup = popups.find((popup) => popup.page[0] === toName);
      if (!popup) return; // 現ページ対象のポップアップが存在しない場合はreturn

      const { id, target } = popup;
      const isReadPopup = readPopupList.includes(id);
      if (isReadPopup) return; // 既読ポップアップの場合はreturn

      const isPremium = await isBilling($store);

      switch (target[0]) {
        default:
        case "all":
          break;
        case "free":
          if (isPremium) return;
          break;
        case "premium":
          if (!isPremium) return;
          break;
      }

      // ポップアップを表示
      const dialog = new Dialog(PopupDialog);
      const data: PopupDialogProps = {
        popup,
      };
      dialog.show(data);

      // ポップアップを既読にする
      $store.dispatch("generalModule/readPopup", id);
    },
  },
});

interface Props {}

interface Data {
  popups: Popup[];
}

interface Computed {
  readPopupList: string[];
  plan: Plan;
}

interface Methods {
  close(id: number): void;
  showErrorDialog(): Promise<void>;
  persistStorage(): Promise<void>;
}
